import { RtcUtilsSettings } from "./settings";

/**
 * Identifies the severity of a log message (e.g: a warning or an error).
 */
export const enum LogLevel {
    INFO = "INFO",
    WARN = "WARN",
    DEBUG = "DEBUG",
    ERROR = "ERROR"
}

/**
 * Details of the log messages provided in the LogCallback.
 */
export declare interface LogData {
    /** Formatted string of timestamp (local time) at which log message was generated.
     *  Format:  "YYYY-MM-DD HH:MM::SS.XYZ" where XYZ is milliseconds and HH is in 24 hour format. */
    timestamp: string;
    /** Severity of log. */
    level: LogLevel;
    /** Source file and module of log. */
    tag: string;
    /** Log text. */
    message: string;
}

/**
 * Callback to be executed by the StreamKit/SessionControl and other libraries to deliver logs to the client.
 * Refer registerLogCallback API of StreamKit/SessionControl.
 */
export type LogCallback = (log: LogData) => void;

type LogFunction = (tag: string, msg: string, ...jsonObject: any[]) => void;
type FormatFunction = (msg: string, ...args: any[]) => string;

// This interface is required because test client accesses LogImpl.
declare interface LogInterface {
    d: LogFunction;
    w: LogFunction;
    e: LogFunction;
    i: LogFunction;
    format: FormatFunction;
    setLogCallback(callback?: LogCallback): void;
    isCallbackRegistered(): boolean;
}
// Only exported for dev client:
export class LogImpl implements LogInterface {
    // A no-op logging function for when logging is disabled
    private _nop: LogFunction;
    // Cached closures for emitted logging.
    private _d: LogFunction;
    private _i: LogFunction;
    private _w: LogFunction;
    private _e: LogFunction;
    private _f: FormatFunction;
    private queue: LogData[] = [];
    private callback?: LogCallback;
    private module: string = "R";
    static readonly MAX_QUEUE_SIZE = 50;

    constructor(module?: string) {
        this._nop = (tag: string, msg: string, ...jsonObject: any[]) => {};
        this._d = (tag: string, msg: string, ...jsonObject: any[]) =>
            this.handleLog(LogLevel.DEBUG, tag, msg, jsonObject);
        this._i = (tag: string, msg: string, ...jsonObject: any[]) =>
            this.handleLog(LogLevel.INFO, tag, msg, jsonObject);
        this._w = (tag: string, msg: string, ...jsonObject: any[]) =>
            this.handleLog(LogLevel.WARN, tag, msg, jsonObject);
        this._e = (tag: string, msg: string, ...jsonObject: any[]) =>
            this.handleLog(LogLevel.ERROR, tag, msg, jsonObject);
        this._f = (msg: string, ...args: any[]) => {
            return this.formatString(msg, ...args);
        };

        this.module = module ?? this.module;
    }

    public get d(): LogFunction {
        if (RtcUtilsSettings.consoleLoggingEnabled) {
            return console.debug.bind(console, "%s DEBUG [%s] %s@@", this.renderDate(new Date()));
        } else {
            return RtcUtilsSettings.loggingEnabled ? this._d : this._nop;
        }
    }

    public get w(): LogFunction {
        if (RtcUtilsSettings.consoleLoggingEnabled) {
            return console.warn.bind(console, "%s WARN  [%s] %s@@", this.renderDate(new Date()));
        } else {
            return RtcUtilsSettings.loggingEnabled ? this._w : this._nop;
        }
    }

    public get i(): LogFunction {
        if (RtcUtilsSettings.consoleLoggingEnabled) {
            return console.info.bind(console, "%s INFO  [%s] %s@@", this.renderDate(new Date()));
        } else {
            return RtcUtilsSettings.loggingEnabled ? this._i : this._nop;
        }
    }

    public get e(): LogFunction {
        if (RtcUtilsSettings.consoleLoggingEnabled) {
            return console.error.bind(console, "%s ERROR [%s] %s@@", this.renderDate(new Date()));
        } else {
            // Always emit errors
            return this._e;
        }
    }

    /**
     * Register the callback function to receive log messages.
     * Clients can invoke Log.setLogCallback() to clear the registered callback(If needed).
     * @param callback - function which can handle the log messages.
     * @note Once a callback is registered this function delivers all the queued log messages in callback asynchronously.
     */
    public setLogCallback(callback?: LogCallback): void {
        this.callback = callback;
        if (this.callback && this.queue.length) {
            window.setTimeout(() => {
                this.executeLogCallback(true);
            });
        }
    }

    /**
     * Informs if the clients have registered callback with this object.
     * @returns true if a callback is registered.
     * @note This is a temporary function required in GridServer and Ragnarok1.0 libraries.
     */
    public isCallbackRegistered(): boolean {
        return !!this.callback;
    }

    public sanitize(msg?: string): string | undefined {
        if (!msg) {
            return msg;
        }

        interface SanitizationParams {
            secretBlock: RegExp;
            replaceStr: string;
        }
        const sanitizationGroups: SanitizationParams[] = [
            {
                secretBlock: /(partnerCustomData" *: *")((\\"|[^"])*)(?=")/g,
                replaceStr: "$1***"
            },
            {
                secretBlock: /("clientIp" *: *")(\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?=")/g,
                replaceStr: "$1***"
            }
        ];

        for (const group of sanitizationGroups) {
            msg = msg.replace(group.secretBlock, group.replaceStr);
        }
        return msg;
    }

    public get format(): FormatFunction {
        return this._f;
    }

    public stringifyArgs(...args: any[]): string {
        if (args.length) {
            return `##${JSON.stringify(args)}`;
        }
        return "";
    }

    /// Formats a string with substitutions {<digit>}
    private formatString(str: string, ...args: any[]) {
        return str.replace(/{(\d+)}/g, function (match, number, _offset, _string) {
            return typeof args[number] != "undefined" ? args[number] : match;
        });
    }

    /**
     * Delivers the first log in queue via callback.
     * If callback is not set or queue is empty then this function is a no-op.
     * @param recursive - If true, queues a task to deliver next log via callback if the queue has more elements.
     */
    private executeLogCallback(recursive: boolean): void {
        if (this.callback && this.queue.length) {
            this.callback(this.queue[0]);
            this.queue.shift();

            if (recursive && this.queue.length) {
                // execute the next log through window.setTimeout.
                window.setTimeout(() => {
                    this.executeLogCallback(true);
                });
            }
        }
    }

    private handleLog(level: LogLevel, tag: string, msg: string, args: any[]): void {
        let logMsg = `${msg}${this.stringifyArgs(...args)}`;

        let data: LogData = {
            timestamp: this.renderDate(new Date()),
            level: level,
            tag: this.module + "/" + tag,
            message: logMsg
        };

        this.queue.push(data);
        if (this.callback) {
            // On some devices doing a console.log is expensive. Ex: QosKPI systems.
            // The normal flow of Ragnarok calls should be free from this logging dependency as it might affect the streaming metrics.
            // Execute log callbacks asynchronously, this frees up the main thread to execute other queued tasks.
            window.setTimeout(() => {
                this.executeLogCallback(false);
            });
        } else {
            if (this.queue.length > LogImpl.MAX_QUEUE_SIZE) {
                this.queue.shift();
            }
        }
    }

    private renderDate(date: Date): string {
        const lpad = (value: string, chars: number, padWith: string): string => {
            const howMany = chars - value.length;
            if (howMany > 0) {
                let res: string = "";
                for (let i = 0; i < howMany; i++) {
                    res += padWith;
                }
                res += value;
                return res;
            }
            return value;
        };

        const fullYear = (d: Date): string => {
            return lpad(d.getFullYear().toString(), 4, "0");
        };

        const month = (d: Date): string => {
            return lpad((d.getMonth() + 1).toString(), 2, "0");
        };

        const day = (d: Date): string => {
            return lpad(d.getDate().toString(), 2, "0");
        };

        const hours = (d: Date): string => {
            return lpad(d.getHours().toString(), 2, "0");
        };

        const minutes = (d: Date): string => {
            return lpad(d.getMinutes().toString(), 2, "0");
        };

        const seconds = (d: Date): string => {
            return lpad(d.getSeconds().toString(), 2, "0");
        };

        const millis = (d: Date): string => {
            return lpad(d.getMilliseconds().toString(), 3, "0");
        };

        const dateSeparator = "-";
        let ds: string = "";
        // yyyy-mm-dd hh:mm:ss,m
        ds =
            fullYear(date) +
            dateSeparator +
            month(date) +
            dateSeparator +
            day(date) +
            " " +
            hours(date) +
            ":" +
            minutes(date) +
            ":" +
            seconds(date) +
            "." +
            millis(date);

        return ds;
    }
}

// Singleton LogImpl instance. All users of LogImpl should use this instead.
export let Log = new LogImpl();

/**
 * Registers a callback to deliver the log messages.
 * This API should be invoked before executing any functions of SessionControl/StreamKit library.
 * Clients can invoke registerLogCallback() to clear the registered callback(If needed).
 * @param callback - function to be executed to deliver log.
 */
export function registerLogCallback(callback?: LogCallback): void {
    Log.setLogCallback(callback);
}
